import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import dayjs from "dayjs";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import { MCButton } from "../../../../../MainComponents/Button/Button";
import ModalGraphv2 from "../../../../../Modal/ModalGraphv2";
import { utilitiesDashboard } from "../../../../../../apis/apiDashboard";

const DateSelectorWithButton = ({
  chartDates,
  setChartDates,
  startDate,
  endDate,
  onRangeChange,
  onchartDates,
  fetchData,
  ingroups,
  selectedIngroup,
  onIngroupChange,
}) => {
  const [t] = useTranslation("dashboard");
  const handleChangeDate = (value) => {
    onchartDates(value); // notifica al padre
  };

  const [open, setOpen] = useState(false);
  const [utilities, setUtilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    utilitiesDashboard()
      .then((res) => {
        setUtilities(res?.utilities || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);
  return (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <MCButton
          label={t("dashboard.add_chart_button")}
          variant="primary"
          outline
          onClick={() => setOpen(true)}
        />
        {/* Aquí llamas al modal */}

        <ModalGraphv2
          show={open}
          onHide={() => setOpen(false)}
          utilities={utilities}
          onSuccess={fetchData}
        />
        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel id="fecha-label">{t("dashboard.date_label")}</InputLabel>
          <Select
            labelId="fecha-label"
            id="fecha"
            value={chartDates}
            label={t("dashboard.date_label")}
            onChange={(e) => handleChangeDate(e.target.value)}
          >
            <MenuItem value="" disabled>
              {t("dashboard.select_range_placeholder")}.
            </MenuItem>
            <MenuItem value={30}>{t("dashboard.last_30_days")}</MenuItem>
            <MenuItem value={60}>{t("dashboard.last_60_days")}</MenuItem>
            <MenuItem value={90}>{t("dashboard.last_90_days")}</MenuItem>
            <MenuItem value={180}>{t("dashboard.last_half_year")}</MenuItem>
            <MenuItem value="range">{t("dashboard.range_option")}</MenuItem>
          </Select>
        </FormControl>

        {ingroups && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="select-ingroup-label">
              {t("Graphics.group")}
            </InputLabel>
            <Select
              labelId="select-ingroup-label"
              value={selectedIngroup}
              label={t("Graphics.group")}
              onChange={(e) => onIngroupChange(e.target.value)}
            >
              {Object.entries(ingroups).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {chartDates === "range" &&
        (() => {
          const today = dayjs().startOf("day");
          const startDayjs = startDate ? dayjs(startDate) : null;
          const endDayjs = endDate ? dayjs(endDate) : null;

          const dayPlaceholder = t("dashboard.day_placeholder");
          const monthPlaceholder = t("dashboard.month_placeholder");
          const yearPlaceholder = t("dashboard.year_placeholder");
          const datePickerLocaleText = {
            fieldDayPlaceholder: () => dayPlaceholder,
            fieldMonthPlaceholder: ({ contentType }) =>
              contentType === "letter" ? monthPlaceholder : "MM",
            fieldYearPlaceholder: () => yearPlaceholder,
          };

          // Start: sin límite inferior; no puede ser posterior a la fecha fin (ni a hoy).
          const startMaxDate = endDayjs || today;

          // End: no puede ser anterior al inicio ni posterior a hoy.
          const endMinDate = startDayjs || undefined;
          const endMaxDate = today;

          // Validación del rango: máximo un año entre inicio y fin.
          const hasBothDates = !!(startDayjs && endDayjs);
          const rangeExceedsYear =
            hasBothDates &&
            endDayjs.isAfter(startDayjs.add(1, "year"), "day");

          const endFutureError =
            endDayjs && endDayjs.isAfter(today, "day");
          const endBeforeStartError =
            hasBothDates && endDayjs.isBefore(startDayjs, "day");

          const endError =
            rangeExceedsYear || endFutureError || endBeforeStartError;
          const endHelperText = rangeExceedsYear
            ? t("dashboard.range_year_helper")
            : endFutureError
              ? t("dashboard.end_date_helper")
              : "";

          const startError = rangeExceedsYear || endBeforeStartError;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ width: 220 }}>
                <EGDatePicker
                  label={t("dashboard.start_date_label")}
                  value={startDayjs}
                  format={t("dashboard.date_format")}
                  localeText={datePickerLocaleText}
                  maxDate={startMaxDate}
                  onChange={(newValue) => {
                    onRangeChange(
                      "start",
                      newValue ? newValue.toDate() : null,
                    );
                  }}
                  isValid={startError ? false : null}
                />
              </Box>
              <Box sx={{ width: 220 }}>
                <EGDatePicker
                  label={t("dashboard.end_date_label")}
                  value={endDayjs}
                  format={t("dashboard.date_format")}
                  localeText={datePickerLocaleText}
                  minDate={endMinDate}
                  maxDate={endMaxDate}
                  onChange={(newValue) => {
                    onRangeChange(
                      "end",
                      newValue ? newValue.toDate() : null,
                    );
                  }}
                  isValid={endError ? false : null}
                />
                {endHelperText && (
                  <FormHelperText error sx={{ fontSize: "0.7rem", mx: 1.5 }}>
                    {endHelperText}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          );
        })()}
    </Paper>
  );
};

export default DateSelectorWithButton;
